import { TicketIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { Loader } from '@/components/ui'
import { useToast } from '@/hooks'
import { useHelpActions, useHelpInitialTicketId } from '@/modules/help/store'
import { cn } from '@/utils'
import { useSupportTicket } from '../hooks/useSupportTicket'
import {
  useSupportActions,
  useSupportActiveTicket,
  useSupportLoaders,
  useSupportLoadingTickets,
  useSupportTickets,
} from '../store'
import type { SupportTicket } from '../types'
import { TicketList } from './TicketList'
import { TicketThread } from './TicketThread'

// The ticket the user most likely came for: the newest one still waiting on us,
// else the newest one waiting on their reply. List order is newest message first.
const ticketToAutoOpen = (tickets: SupportTicket[]): SupportTicket | null =>
  tickets.find((ticket) => ticket.status === 'open') ??
  tickets.find((ticket) => ticket.status === 'awaiting_user') ??
  null

export const TicketsPane = () => {
  const tickets = useSupportTickets()
  const loading = useSupportLoadingTickets()
  const activeTicket = useSupportActiveTicket()
  const { clearActiveTicket } = useSupportActions()
  const { fetchTickets, fetchTicket } = useSupportLoaders()
  const initialTicketId = useHelpInitialTicketId()
  const { close, consumeInitialTicket } = useHelpActions()
  const { openTicket } = useSupportTicket()
  const { errorToast } = useToast()

  const openThread = (publicId: string) =>
    fetchTicket(publicId).catch((error: Error) =>
      errorToast('Failed to load ticket', error),
    )

  useEffect(() => {
    fetchTickets().catch(() => {})
  }, [fetchTickets])

  useEffect(() => {
    if (!initialTicketId) return
    openThread(initialTicketId)
    consumeInitialTicket()
  }, [initialTicketId, consumeInitialTicket])

  // Opening the pane cold lands on a thread, not an empty list. A deep open or a
  // resumed thread wins, and going back to the list must not re-open it.
  const autoOpened = useRef(false)
  useEffect(() => {
    if (autoOpened.current) return
    if (initialTicketId || activeTicket) {
      autoOpened.current = true
      return
    }
    if (loading || tickets.length === 0) return
    autoOpened.current = true
    const ticket = ticketToAutoOpen(tickets)
    if (ticket) openThread(ticket.publicId)
  }, [initialTicketId, activeTicket, loading, tickets])

  const openNewTicket = () => {
    close()
    openTicket()
  }

  // A deep open still has the last thread in the store. Showing it would paint
  // the wrong conversation and stamp it read, burying a reply nobody saw.
  const pendingDeepOpen =
    initialTicketId !== null && activeTicket?.publicId !== initialTicketId
  const thread = pendingDeepOpen ? null : activeTicket
  const showsThreadPane = thread !== null || pendingDeepOpen

  return (
    <div className="flex min-h-0 flex-1">
      <div
        className={cn(
          'w-full flex-col md:flex md:w-80 md:shrink-0 md:border-r md:border-border',
          showsThreadPane ? 'hidden' : 'flex',
        )}
      >
        <TicketList
          tickets={tickets}
          loading={loading}
          selectedPublicId={thread?.publicId ?? null}
          onSelect={openThread}
          onNewTicket={openNewTicket}
        />
      </div>

      <div
        className={cn(
          'min-w-0 flex-1 flex-col md:flex',
          showsThreadPane ? 'flex' : 'hidden',
        )}
      >
        {thread && <TicketThread onBack={clearActiveTicket} />}

        {pendingDeepOpen && (
          <div className="flex min-h-0 flex-1 items-center justify-center p-8">
            <Loader />
          </div>
        )}

        {!showsThreadPane && (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
            <TicketIcon className="h-8 w-8 text-blue-4" />
            <p className="text-sm text-ink-subtle text-pretty">
              Select a ticket to read the conversation.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
